'use client';
import Components from '@/components/component_registry';
import { InlineEditProvider } from '@/components/atoms/InlineEditContext';
import { normalizeSvgFileForUpload } from '@/utils/normalizeSvgUpload';
import { collectUsedImages } from './helpers/altTextAudit';
import {
    LARGE_IMAGE_SIZE_LIMIT,
    readImageSizeBytes,
} from './helpers/imageSize';
import {
    ArrowDownIcon,
    ArrowTopRightOnSquareIcon,
    ArrowUpIcon,
    BoltIcon,
    DocumentDuplicateIcon,
    MegaphoneIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
} from '@heroicons/react/20/solid';
import { useEffect, useMemo, useRef, useState } from 'react';
import ComponentEditor from './ComponentEditor';
import InlineImageControls from './InlineImageControls';
import { useEditor } from './context/EditorContext/EditorContext';
import { useNotifications } from './context/NotificationContext/NotificationContext';

const isScrollableNode = (node) => {
    if (!node || typeof window === 'undefined') return false;
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY || style.overflow;
    const canScroll =
        overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'overlay';
    return canScroll && node.scrollHeight > node.clientHeight;
};

const collectScrollableAncestors = (startNode) => {
    const nodes = [];
    let current = startNode?.parentElement || null;

    while (current) {
        if (isScrollableNode(current)) nodes.push(current);
        current = current.parentElement;
    }

    return nodes;
};

const formatComponentLabel = (name) =>
    String(name || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim();

const ITEM_GRID_FULL_OVERFLOW_SPACING = new Set(['overflow']);

const findTextFieldKey = (value, target, path = '') => {
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value) === target && path ? path : null;
    }

    if (!value || typeof value !== 'object') return null;

    if (Array.isArray(value)) {
        for (let index = 0; index < value.length; index += 1) {
            const key = findTextFieldKey(
                value[index],
                target,
                `${path}[${index}]`
            );
            if (key) return key;
        }
        return null;
    }

    for (const [property, child] of Object.entries(value)) {
        const key = findTextFieldKey(
            child,
            target,
            path ? `${path}.${property}` : property
        );
        if (key) return key;
    }

    return null;
};

const normalizeImagePath = (value) => {
    try {
        const parsed = new URL(
            String(value || '').trim(),
            'https://editor.local'
        );
        const optimizedUrl = parsed.searchParams.get('url');
        if (optimizedUrl && parsed.pathname.includes('/_next/image')) {
            return normalizeImagePath(optimizedUrl);
        }
        return decodeURIComponent(parsed.pathname)
            .replace(/\/+$/, '')
            .toLowerCase();
    } catch {
        return String(value || '')
            .trim()
            .replace(/[?#].*$/, '')
            .replace(/\/+$/, '')
            .toLowerCase();
    }
};

const findLibraryImage = (images, url) => {
    const normalized = normalizeImagePath(url);
    return (
        images.find(
            (candidate) =>
                String(candidate?.url || '').trim() === String(url || '').trim()
        ) ||
        images.find(
            (candidate) => normalizeImagePath(candidate?.url) === normalized
        ) ||
        null
    );
};

function ShapeDividerIcon({ className = 'h-3.5 w-3.5', flipped = false }) {
    return (
        <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
            className={`${className} ${flipped ? '-scale-y-100' : ''}`}
        >
            <path
                d="M3.25 10.75C6.5 7.5 13.5 7.5 16.75 10.75"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
        </svg>
    );
}

const coerceBoolean = (value, fallback = true) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    }
    return fallback;
};

const getVisibleEditorNavBottom = () => {
    if (typeof window === 'undefined') return 0;

    const navNodes = Array.from(
        document.querySelectorAll('[data-component="Nav"]')
    );

    return navNodes.reduce((bottom, node) => {
        if (node.getAttribute('data-editor-visible') === 'false') {
            return bottom;
        }

        const rect = node.getBoundingClientRect();
        const style = window.getComputedStyle(node);
        const isVisible =
            rect.width > 0 &&
            rect.height > 0 &&
            rect.bottom > 0 &&
            rect.top < window.innerHeight &&
            style.opacity !== '0' &&
            style.visibility !== 'hidden' &&
            style.pointerEvents !== 'none';

        if (!isVisible) return bottom;
        return Math.max(bottom, rect.bottom);
    }, 0);
};

/**
 * Manages the component level state for the editor// diff to trigger build
 */
export function EditableComponent(props) {
    const { index } = props;
    const fixedRailHeight = 40;
    const fixedRailInset = 12;
    const navRailInset = 8;

    const editor = useEditor();
    const notifications = useNotifications();

    const component = editor.components.data[index];
    const previousComponent = editor.components.data[index - 1];
    const nextComponent = editor.components.data[index + 1];
    const componentLabel = formatComponentLabel(component?.name);
    const itemGridVerticalSpacing = String(
        component?.props?.VerticalSpacing &&
            typeof component.props.VerticalSpacing === 'object' &&
            'value' in component.props.VerticalSpacing
            ? component.props.VerticalSpacing.value
            : component?.props?.VerticalSpacing || ''
    ).trim();
    const isItemGridOverflowComponent =
        component?.name === 'ItemGrid' &&
        ITEM_GRID_FULL_OVERFLOW_SPACING.has(itemGridVerticalSpacing);
    const railDefaultPadding = isItemGridOverflowComponent ? 0 : 44;
    const railDefaultStickyTop = isItemGridOverflowComponent ? 112 : 136;
    const railMinHeight = Math.max(
        44,
        Math.round(componentLabel.length * 9 + 28)
    );
    const railBadgeHeight = Math.max(
        38,
        Math.round(componentLabel.length * 5.5 + 1)
    );
    const navRailMinHeight = Math.max(68, railBadgeHeight + 18);
    const wrapperRef = useRef(null);
    const [hasMounted, setHasMounted] = useState(false);
    const [railClearance, setRailClearance] = useState({
        paddingTop: railDefaultPadding,
        stickyTop: railDefaultStickyTop,
    });
    const [itemGridOverflowRailTop, setItemGridOverflowRailTop] =
        useState(null);
    const [fixedRailState, setFixedRailState] = useState({
        left: 0,
        top: 12,
        visible: false,
    });
    const [mediaFieldRequest, setMediaFieldRequest] = useState(null);
    const [bannerEditorOpen, setBannerEditorOpen] = useState(false);
    const imageRequestIdRef = useRef(0);

    const is_movable =
        component.data_file !== 'nav.json' &&
        component.data_file !== 'footer.json';
    const isNavComponent = component.data_file === 'nav.json';
    const isFooterComponent = component.data_file === 'footer.json';
    const isReviewFloatComponent = component.name === 'ReviewFloat';
    const isPopupComponent = component.name === 'Popup';
    const isFloatingActionComponent = component.name === 'FloatingAction';
    const isShapeDivider = component.name === 'ShapeDivider';
    const previousComponentIsReviewFloat =
        previousComponent?.name === 'ReviewFloat';
    const nextComponentIsReviewFloat = nextComponent?.name === 'ReviewFloat';
    const reviewFloatControlsOnLeft =
        isReviewFloatComponent &&
        String(component.props?.Alignment || '').trim() === 'right';
    const isFixedRailComponent = isNavComponent;
    const isAnyComponentEditorOpen = Array.isArray(editor.components.data)
        ? editor.components.data.some((_, componentIndex) =>
              editor.components.visible({ index: componentIndex })
          )
        : false;
    const popupComponentIndex = isFooterComponent
        ? editor.components.data.findIndex((item) => item?.name === 'Popup')
        : -1;
    const popupComponent =
        popupComponentIndex >= 0
            ? editor.components.data[popupComponentIndex]
            : null;
    const popupEnabled = coerceBoolean(popupComponent?.props?.Enabled, true);
    const floatingActionComponentIndex = isFooterComponent
        ? editor.components.data.findIndex(
              (item) => item?.name === 'FloatingAction'
          )
        : -1;
    const floatingActionComponent =
        floatingActionComponentIndex >= 0
            ? editor.components.data[floatingActionComponentIndex]
            : null;
    const floatingActionEnabled = coerceBoolean(
        floatingActionComponent?.props?.Enabled,
        true
    );

    useEffect(() => {
        if (bannerEditorOpen && isAnyComponentEditorOpen) {
            setBannerEditorOpen(false);
        }
    }, [bannerEditorOpen, isAnyComponentEditorOpen]);

    useEffect(() => {
        if (!isNavComponent || typeof window === 'undefined') return undefined;

        let frameId = 0;
        let intervalId = 0;
        const scrollTargets = new Set();

        const measure = () => {
            frameId = window.requestAnimationFrame(() => {
                const targetNode = isNavComponent
                    ? wrapperRef.current?.querySelector(
                          '[data-component="Nav"]'
                      )
                    : wrapperRef.current;

                if (!targetNode) {
                    setFixedRailState((prev) =>
                        prev.visible ? { ...prev, visible: false } : prev
                    );
                    return;
                }

                const rect = targetNode.getBoundingClientRect();
                const style = window.getComputedStyle(targetNode);
                const editorVisible = isNavComponent
                    ? targetNode.getAttribute('data-editor-visible') !== 'false'
                    : true;
                const visible =
                    editorVisible &&
                    rect.width > 0 &&
                    rect.height > 0 &&
                    rect.bottom > 0 &&
                    rect.top < window.innerHeight &&
                    style.opacity !== '0' &&
                    style.visibility !== 'hidden' &&
                    style.pointerEvents !== 'none';

                if (!visible) {
                    setFixedRailState((prev) =>
                        prev.visible ? { ...prev, visible: false } : prev
                    );
                    return;
                }

                let railVisualHeight = fixedRailHeight;
                if (isNavComponent) railVisualHeight = navRailMinHeight;
                const nextTop = isNavComponent
                    ? (() => {
                          const centeredTop =
                              rect.top + (rect.height - railVisualHeight) / 2;
                          const minTop = Math.max(
                              fixedRailInset,
                              rect.top + navRailInset
                          );
                          const maxTop = Math.min(
                              window.innerHeight -
                                  railVisualHeight -
                                  fixedRailInset,
                              rect.bottom - railVisualHeight - navRailInset
                          );

                          if (maxTop < minTop) {
                              return Math.min(
                                  Math.max(centeredTop, fixedRailInset),
                                  Math.max(
                                      fixedRailInset,
                                      window.innerHeight -
                                          railVisualHeight -
                                          fixedRailInset
                                  )
                              );
                          }

                          return Math.min(
                              Math.max(centeredTop, minTop),
                              maxTop
                          );
                      })()
                    : Math.min(
                          Math.max(rect.top + fixedRailInset, fixedRailInset),
                          Math.max(
                              fixedRailInset,
                              window.innerHeight -
                                  fixedRailHeight -
                                  fixedRailInset
                          )
                      );
                const nextLeft = Math.max(0, rect.left);

                setFixedRailState((prev) => {
                    if (
                        prev.visible &&
                        prev.left === nextLeft &&
                        prev.top === nextTop
                    ) {
                        return prev;
                    }

                    return {
                        left: nextLeft,
                        top: nextTop,
                        visible: true,
                    };
                });
            });
        };

        measure();
        intervalId = window.setInterval(measure, 150);
        scrollTargets.add(window);
        if (document.documentElement)
            scrollTargets.add(document.documentElement);
        if (document.body) scrollTargets.add(document.body);
        if (document.scrollingElement)
            scrollTargets.add(document.scrollingElement);
        collectScrollableAncestors(wrapperRef.current).forEach((node) =>
            scrollTargets.add(node)
        );
        scrollTargets.forEach((target) => {
            try {
                target?.addEventListener('scroll', measure, { passive: true });
            } catch {
                // Ignore inaccessible targets.
            }
        });
        window.addEventListener('resize', measure);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.clearInterval(intervalId);
            scrollTargets.forEach((target) => {
                try {
                    target?.removeEventListener('scroll', measure);
                } catch {
                    // Ignore inaccessible targets.
                }
            });
            window.removeEventListener('resize', measure);
        };
    }, [isNavComponent, navRailMinHeight]);

    useEffect(() => {
        if (
            isNavComponent ||
            isShapeDivider ||
            isReviewFloatComponent ||
            isPopupComponent ||
            isFloatingActionComponent ||
            typeof window === 'undefined'
        ) {
            return undefined;
        }

        let frameId = 0;
        const scrollTargets = new Set();

        const measure = () => {
            window.cancelAnimationFrame(frameId);
            frameId = window.requestAnimationFrame(() => {
                const rect = wrapperRef.current?.getBoundingClientRect();
                if (!rect) return;

                if (isItemGridOverflowComponent) {
                    const cards = Array.from(
                        wrapperRef.current?.querySelectorAll(
                            '[data-itemgrid-feature-card="true"]'
                        ) || []
                    );
                    const cardBounds = cards.reduce(
                        (bounds, card) => {
                            const cardRect = card.getBoundingClientRect();
                            if (!cardRect.width || !cardRect.height) {
                                return bounds;
                            }

                            return {
                                top: Math.min(bounds.top, cardRect.top),
                                bottom: Math.max(
                                    bounds.bottom,
                                    cardRect.bottom
                                ),
                            };
                        },
                        {
                            top: Number.POSITIVE_INFINITY,
                            bottom: Number.NEGATIVE_INFINITY,
                        }
                    );
                    const nextRailTop =
                        Number.isFinite(cardBounds.top) &&
                        Number.isFinite(cardBounds.bottom)
                            ? Math.round(
                                  (cardBounds.top + cardBounds.bottom) / 2 -
                                      rect.top
                              )
                            : null;

                    setItemGridOverflowRailTop((prev) =>
                        prev === nextRailTop ? prev : nextRailTop
                    );
                } else {
                    setItemGridOverflowRailTop((prev) =>
                        prev === null ? prev : null
                    );
                }

                const navBottom = getVisibleEditorNavBottom();
                const basePadding = railDefaultPadding;
                const clearanceGap = 16;
                const nextPaddingTop = Math.max(
                    basePadding,
                    Math.ceil(navBottom - rect.top + clearanceGap)
                );
                const nextStickyTop = Math.max(
                    railDefaultStickyTop,
                    Math.ceil(navBottom + clearanceGap)
                );

                setRailClearance((prev) => {
                    if (
                        prev.paddingTop === nextPaddingTop &&
                        prev.stickyTop === nextStickyTop
                    ) {
                        return prev;
                    }

                    return {
                        paddingTop: nextPaddingTop,
                        stickyTop: nextStickyTop,
                    };
                });
            });
        };

        measure();
        scrollTargets.add(window);
        if (document.documentElement)
            scrollTargets.add(document.documentElement);
        if (document.body) scrollTargets.add(document.body);
        if (document.scrollingElement)
            scrollTargets.add(document.scrollingElement);
        collectScrollableAncestors(wrapperRef.current).forEach((node) =>
            scrollTargets.add(node)
        );
        scrollTargets.forEach((target) => {
            try {
                target?.addEventListener('scroll', measure, { passive: true });
            } catch {
                // Ignore inaccessible targets.
            }
        });
        window.addEventListener('resize', measure);

        return () => {
            window.cancelAnimationFrame(frameId);
            scrollTargets.forEach((target) => {
                try {
                    target?.removeEventListener('scroll', measure);
                } catch {
                    // Ignore inaccessible targets.
                }
            });
            window.removeEventListener('resize', measure);
        };
    }, [
        isNavComponent,
        isPopupComponent,
        isFloatingActionComponent,
        isItemGridOverflowComponent,
        isReviewFloatComponent,
        isShapeDivider,
        railDefaultPadding,
        railDefaultStickyTop,
    ]);

    if (!component.name) {
        return <div>Invalid Component: {JSON.stringify(component)}</div>;
    }

    const Component = Components[component.name];
    const previousShapeDividerIndex =
        previousComponent?.name === 'ShapeDivider' ? index - 1 : null;
    const nextShapeDividerIndex =
        nextComponent?.name === 'ShapeDivider' ? index + 1 : null;
    const showShapeDividerButton =
        !isShapeDivider && !isPopupComponent && !isFloatingActionComponent;
    const handleBeforeShapeDividerAction = () => {
        editor.components.add_panel.close();
        if (previousShapeDividerIndex !== null) {
            editor.components.open({
                index: previousShapeDividerIndex,
            });
            return;
        }

        editor.components.add({
            index,
            component_name: 'ShapeDivider',
        });
        editor.components.open({
            index,
        });
    };
    const handleAfterShapeDividerAction = () => {
        editor.components.add_panel.close();
        if (nextShapeDividerIndex !== null) {
            editor.components.open({
                index: nextShapeDividerIndex,
            });
            return;
        }

        editor.components.add({
            index: index + 1,
            component_name: 'ShapeDivider',
        });
        editor.components.open({
            index: index + 1,
        });
    };
    const railHiddenClass =
        'translate-x-0 opacity-100 transition-all duration-200 ease-out md:-translate-x-full md:opacity-0 md:delay-700 md:duration-300 md:group-hover/editor-rail:translate-x-0 md:group-hover/editor-rail:opacity-100 md:group-hover/editor-rail:delay-0 md:group-hover/editor-rail:duration-200 md:group-focus-within/editor-rail:translate-x-0 md:group-focus-within/editor-rail:opacity-100 md:group-focus-within/editor-rail:delay-0 md:group-focus-within/editor-rail:duration-200';
    const railStackClass = 'relative inline-flex';
    const railShellClass =
        'relative inline-flex flex-col items-center gap-1 rounded-l-none rounded-r-[16px] border border-l-0 border-black/30 bg-neutral-800 p-1.5 text-white shadow-[0_16px_32px_rgba(15,23,42,0.32)]';
    const railShellCenteredClass =
        'relative inline-flex flex-col items-center justify-center gap-1 rounded-l-none rounded-r-[16px] border border-l-0 border-black/30 bg-neutral-800 p-1.5 text-white shadow-[0_16px_32px_rgba(15,23,42,0.32)]';
    const railLabelWrapClass =
        'pointer-events-none absolute left-full top-4 -ml-px flex';
    const railLabelWrapCenteredClass =
        'pointer-events-none absolute left-full top-1/2 -ml-px flex -translate-y-1/2';
    const railLabelClass =
        'flex w-[16px] items-center justify-center rounded-l-none rounded-r-[5px] bg-gradient-to-b from-primary-700 to-secondary-700 text-white shadow-[0_6px_18px_rgba(143,82,241,0.28)]';
    const railLabelTextClass =
        'block -rotate-90 whitespace-nowrap text-[6px] font-semibold uppercase tracking-[0.08em] leading-none';
    const railContainerClass = isItemGridOverflowComponent
        ? 'group/editor-rail pointer-events-none absolute left-0 z-[60] w-32'
        : 'group/editor-rail pointer-events-none absolute inset-y-0 left-0 w-32';
    const railPositionClass = isItemGridOverflowComponent ? '' : 'sticky';
    const railContainerStyle = isItemGridOverflowComponent
        ? {
              top:
                  itemGridOverflowRailTop === null
                      ? '50%'
                      : `${itemGridOverflowRailTop}px`,
              transform: 'translateY(-50%)',
              paddingTop: 0,
              paddingBottom: 0,
          }
        : {
              paddingTop: `${railClearance.paddingTop}px`,
              paddingBottom: `${railDefaultPadding}px`,
          };
    const railStickyStyle = isItemGridOverflowComponent
        ? {}
        : {
              top: `${railClearance.stickyTop}px`,
          };
    const insertButtonHiddenClass =
        'opacity-0 transition-all duration-300 ease-out delay-1000 group-hover:opacity-100 group-hover:duration-500 group-hover:delay-0 group-focus-within:opacity-100 group-focus-within:duration-500 group-focus-within:delay-0';
    const reviewFloatControlHiddenClass =
        'opacity-0 transition-all duration-300 ease-out delay-1000 group-hover/reviewfloat-editor:opacity-100 group-hover/reviewfloat-editor:duration-500 group-hover/reviewfloat-editor:delay-0 group-focus-within/reviewfloat-editor:opacity-100 group-focus-within/reviewfloat-editor:duration-500 group-focus-within/reviewfloat-editor:delay-0';
    const editorOverlayZClass = isItemGridOverflowComponent
        ? 'z-[60]'
        : 'z-[50]';
    const editorOverlayClass = isShapeDivider
        ? '-inset-y-6 inset-x-0'
        : isReviewFloatComponent
        ? 'inset-x-0 top-0 h-[300px] -translate-y-1/2 md:h-[320px] lg:h-[360px]'
        : 'inset-0';
    const reviewFloatPillAnchorClass = reviewFloatControlsOnLeft
        ? 'left-0'
        : 'right-0';
    const reviewFloatPillShellClass = reviewFloatControlsOnLeft
        ? '-translate-x-full rounded-l-[16px] rounded-r-none border border-r-0 md:translate-x-2 md:group-focus-within/reviewfloat-editor:-translate-x-full md:group-hover/reviewfloat-editor:-translate-x-full'
        : 'translate-x-full rounded-l-none rounded-r-[16px] border border-l-0 md:-translate-x-2 md:group-focus-within/reviewfloat-editor:translate-x-full md:group-hover/reviewfloat-editor:translate-x-full';
    const reviewFloatEditorControls =
        hasMounted && isReviewFloatComponent ? (
            <>
                <button
                    className={`pointer-events-auto absolute left-1/2 top-0 z-[55] inline-flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-neutral-800 text-white shadow-[0_10px_24px_rgba(15,23,42,0.28)] ${reviewFloatControlHiddenClass}`}
                    title="Add Before"
                    onClick={() =>
                        editor.components.add_panel.open({
                            index,
                        })
                    }
                >
                    <PlusIcon className="h-3.5 w-3.5" />
                </button>
                <button
                    className={`pointer-events-auto absolute bottom-0 left-1/2 z-[55] inline-flex h-7 w-7 -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-neutral-800 text-white shadow-[0_10px_24px_rgba(15,23,42,0.28)] ${reviewFloatControlHiddenClass}`}
                    title="Add After"
                    onClick={() =>
                        editor.components.add_panel.open({
                            index: index + 1,
                        })
                    }
                >
                    <PlusIcon className="h-3.5 w-3.5" />
                </button>
                <div
                    className={`pointer-events-none absolute top-1/2 z-0 -translate-y-1/2 ${reviewFloatPillAnchorClass}`}
                >
                    <div
                        className={`pointer-events-auto inline-flex flex-col items-center gap-1 border-black/30 bg-neutral-800 p-1.5 text-white opacity-100 shadow-[0_16px_32px_rgba(15,23,42,0.32)] transition-all duration-200 ease-out md:opacity-0 md:delay-700 md:duration-300 md:group-focus-within/reviewfloat-editor:opacity-100 md:group-focus-within/reviewfloat-editor:delay-0 md:group-focus-within/reviewfloat-editor:duration-200 md:group-hover/reviewfloat-editor:opacity-100 md:group-hover/reviewfloat-editor:delay-0 md:group-hover/reviewfloat-editor:duration-200 ${reviewFloatPillShellClass}`}
                    >
                        {is_movable && (
                            <button
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white opacity-100 transition-all ease-in hover:bg-neutral-600"
                                title="Move Up"
                                onClick={() =>
                                    editor.components.move({
                                        index,
                                        above_or_below: -1,
                                    })
                                }
                            >
                                <ArrowUpIcon className="h-3.5 w-3.5" />
                            </button>
                        )}
                        <button
                            className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary-700 text-white opacity-100 transition-all ease-in hover:bg-primary-600 disabled:bg-neutral-500"
                            title="Edit"
                            onClick={() => {
                                editor.components.add_panel.close();
                                editor.components.open({ index });
                            }}
                        >
                            <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                        {is_movable && (
                            <button
                                className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white opacity-100 transition-all ease-in hover:bg-red-600"
                                title="Delete"
                                onClick={() =>
                                    editor.components.delete({
                                        index,
                                    })
                                }
                            >
                                <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                        )}
                        {is_movable && (
                            <button
                                className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white opacity-100 transition-all ease-in hover:bg-neutral-600"
                                title="Move Down"
                                onClick={() =>
                                    editor.components.move({
                                        index,
                                        above_or_below: 1,
                                    })
                                }
                            >
                                <ArrowDownIcon className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            </>
        ) : null;

    useEffect(() => {
        setHasMounted(true);
    }, []);

    const inlineEditValue = useMemo(
        () => ({
            // Nav labels can be rendered inside links or buttons, where a
            // textarea would create invalid nested interactive elements.
            enabled: component.name !== 'Nav',
            // The primary CTA uses a contenteditable span and is safe to edit
            // in place. Keep other Nav text on the component editor surface.
            enabledFieldKeys: component.name === 'Nav' ? ['CTA'] : [],
            componentName: component.name,
            resolveTextFieldKey: (value) =>
                findTextFieldKey(component.props, value),
            onInlineSave: ({ fieldKey, value }) =>
                editor.components.inlineSave({
                    file: component.data_file,
                    key: fieldKey,
                    value,
                }),
        }),
        [
            component.data_file,
            component.name,
            component.props,
            editor.components,
        ]
    );

    const usedImages = useMemo(
        () =>
            collectUsedImages([
                {
                    sourceId: component.data_file,
                    pageName: editor.page.name || 'current',
                    data_file: component.data_file,
                    name: component.name,
                    props: component.props,
                },
            ]),
        [component, editor.page.name]
    );
    const [imageSizesByUrl, setImageSizesByUrl] = useState({});

    useEffect(() => {
        let cancelled = false;
        const libraryImages = Array.isArray(editor.site.data?.images)
            ? editor.site.data.images
            : [];

        Promise.all(
            usedImages.map(async (image) => {
                const libraryImage = findLibraryImage(libraryImages, image.url);
                const librarySize = readImageSizeBytes(libraryImage);
                if (librarySize) return [image.url, librarySize];

                try {
                    const response = await fetch(
                        `/api/image-metadata?url=${encodeURIComponent(
                            image.url
                        )}`
                    );
                    if (!response.ok) return [image.url, 0];
                    const metadata = await response.json();
                    return [image.url, Number(metadata?.sizeBytes || 0)];
                } catch {
                    return [image.url, 0];
                }
            })
        ).then((entries) => {
            if (!cancelled) setImageSizesByUrl(Object.fromEntries(entries));
        });

        return () => {
            cancelled = true;
        };
    }, [editor.site.data?.images, usedImages]);

    const imageWarnings = useMemo(() => {
        const libraryImages = Array.isArray(editor.site.data?.images)
            ? editor.site.data.images
            : [];
        const sizesByUrl = new Map(
            libraryImages
                .filter((image) => image?.url)
                .map((image) => [normalizeImagePath(image.url), image])
        );

        usedImages.forEach((image) => {
            const libraryImage = sizesByUrl.get(normalizeImagePath(image.url));
            const sizeBytes = readImageSizeBytes(libraryImage, {
                sizeBytes: imageSizesByUrl[image.url],
            });
            sizesByUrl.set(normalizeImagePath(image.url), sizeBytes);
        });

        return new Map(
            usedImages.map((image) => [
                image.url,
                {
                    missingAltText: Boolean(image.missingAltText),
                    heavy:
                        sizesByUrl.get(normalizeImagePath(image.url)) >
                        LARGE_IMAGE_SIZE_LIMIT,
                },
            ])
        );
    }, [editor.site.data?.images, imageSizesByUrl, usedImages]);

    const handleImageSeoWarning = ({ focus }) => {
        const ownerWindow = wrapperRef.current?.ownerDocument?.defaultView;
        const targetWindow = ownerWindow?.parent || ownerWindow;
        if (!targetWindow?.dispatchEvent || !ownerWindow?.CustomEvent) return;

        targetWindow.dispatchEvent(
            new ownerWindow.CustomEvent('nicejob:open-alt-text', {
                detail: { focus },
            })
        );
    };

    const handleImageReplace = ({ key, value, isIcon = false }) => {
        imageRequestIdRef.current += 1;
        setMediaFieldRequest({
            id: imageRequestIdRef.current,
            key,
            value,
            inline: true,
            isIcon,
        });
        editor.components.add_panel.close();
        editor.components.open({ index });
    };

    const handleImageUpload = async ({ key, file }) => {
        try {
            const normalizedImage = await normalizeSvgFileForUpload(file);
            const formData = new FormData();
            formData.append('image', normalizedImage);
            const companyId = editor.global.data.company_id;
            const uploadUrl =
                `https://image-search-ytnjkd5upa-uc.a.run.app/upload/image/${companyId}?` +
                new URLSearchParams({
                    filename: file.name.replace(/ +/g, '-').toLowerCase(),
                });
            const response = await fetch(uploadUrl, {
                method: 'POST',
                body: formData,
            });
            if (!response.ok) {
                throw new Error(`Image upload failed: ${response.statusText}`);
            }

            const data = await response.json();
            editor.site.addImage?.({ ...data, size_bytes: file.size });
            editor.components.change({
                file: component.data_file,
                key,
                value: data.url,
            });
            notifications.push({
                title: 'Image uploaded',
                variant: 'success',
                ttl: 6,
            });
        } catch (error) {
            notifications.push({
                title: 'Image upload failed',
                variant: 'error',
                error,
            });
        }
    };

    return (
        <div
            ref={wrapperRef}
            className="edit-component-wrapper group relative flex flex-col justify-center"
        >
            <ComponentEditor
                file={component.data_file}
                componentName={component.name}
                open={editor.components.visible({ index })}
                component_props={component.props}
                mediaFieldRequest={mediaFieldRequest}
                onChange={editor.components.change}
                onDeleteComponent={() =>
                    editor.components.delete({
                        index,
                    })
                }
                onCancel={editor.components.cancel}
                onClose={editor.components.close}
                onSave={editor.save}
            />

            {isNavComponent && (
                <ComponentEditor
                    file={component.data_file}
                    componentName={component.name}
                    editorMode="banner"
                    open={bannerEditorOpen}
                    component_props={component.props}
                    onChange={editor.components.change}
                    onDeleteComponent={() => {}}
                    onCancel={() => setBannerEditorOpen(false)}
                    onClose={() => setBannerEditorOpen(false)}
                    onSave={editor.save}
                />
            )}

            {hasMounted && isFixedRailComponent && fixedRailState.visible && (
                <div
                    className="group/editor-rail pointer-events-none fixed z-[70]"
                    style={{
                        left: `${fixedRailState.left}px`,
                        top: `${fixedRailState.top}px`,
                        width: '128px',
                        height: `${navRailMinHeight}px`,
                        clipPath: 'inset(-96px -96px -96px 0)',
                    }}
                >
                    <div className="pointer-events-auto absolute inset-y-0 left-0 w-32" />
                    <div className={`${railStackClass} ${railHiddenClass}`}>
                        <div className={railLabelWrapCenteredClass}>
                            <span
                                className={railLabelClass}
                                style={{
                                    height: `${railBadgeHeight}px`,
                                }}
                            >
                                <span className={railLabelTextClass}>
                                    {componentLabel}
                                </span>
                            </span>
                        </div>
                        <div
                            className={`pointer-events-auto ${railShellCenteredClass}`}
                            style={{
                                minHeight: `${navRailMinHeight}px`,
                            }}
                        >
                            <button
                                className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white transition-all ease-in hover:bg-secondary-600"
                                title="Edit Banner"
                                onClick={() => {
                                    editor.components.add_panel.close();
                                    editor.components.close();
                                    setBannerEditorOpen(true);
                                }}
                            >
                                <MegaphoneIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                                className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary-700 text-white transition-all ease-in hover:bg-primary-600 disabled:bg-neutral-500"
                                title="Edit"
                                onClick={() => {
                                    editor.components.add_panel.close();
                                    editor.components.open({ index });
                                }}
                            >
                                <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                            {showShapeDividerButton && (
                                <button
                                    className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white transition-all ease-in hover:bg-[#8F52F1]"
                                    title="Add or Edit Shape Divider"
                                    onClick={handleAfterShapeDividerAction}
                                >
                                    <ShapeDividerIcon flipped />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {hasMounted && !isPopupComponent && !isFloatingActionComponent && (
                <div
                    className={`pointer-events-none absolute border-gray-300 ${editorOverlayZClass} ${editorOverlayClass}`}
                >
                    {!isFixedRailComponent &&
                        !isShapeDivider &&
                        !isReviewFloatComponent && (
                            <>
                                {!previousComponentIsReviewFloat && (
                                    <button
                                        className={`pointer-events-auto absolute left-1/2 top-0 z-[55] inline-flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-neutral-800 text-white shadow-[0_10px_24px_rgba(15,23,42,0.28)] ${insertButtonHiddenClass}`}
                                        title="Add Before"
                                        onClick={() =>
                                            editor.components.add_panel.open({
                                                index,
                                            })
                                        }
                                    >
                                        <PlusIcon className="h-3.5 w-3.5" />
                                    </button>
                                )}
                                {!nextComponentIsReviewFloat &&
                                    !isFooterComponent && (
                                        <button
                                            className={`pointer-events-auto absolute bottom-0 left-1/2 z-[55] inline-flex h-7 w-7 -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-neutral-800 text-white shadow-[0_10px_24px_rgba(15,23,42,0.28)] ${insertButtonHiddenClass}`}
                                            title="Add After"
                                            onClick={() =>
                                                editor.components.add_panel.open(
                                                    {
                                                        index: index + 1,
                                                    }
                                                )
                                            }
                                        >
                                            <PlusIcon className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                            </>
                        )}

                    {!isFixedRailComponent &&
                        !isShapeDivider &&
                        !isReviewFloatComponent && (
                            <div
                                className={railContainerClass}
                                style={railContainerStyle}
                            >
                                <div className="pointer-events-auto absolute inset-y-0 left-0 w-32" />
                                <div
                                    className={`${railPositionClass} left-0 mr-auto shrink-0 ${railStackClass} ${railHiddenClass}`}
                                    style={railStickyStyle}
                                >
                                    <div className={railLabelWrapClass}>
                                        <span
                                            className={railLabelClass}
                                            style={{
                                                height: `${railBadgeHeight}px`,
                                            }}
                                        >
                                            <span
                                                className={railLabelTextClass}
                                            >
                                                {componentLabel}
                                            </span>
                                        </span>
                                    </div>
                                    <div
                                        className={`pointer-events-auto ${railShellClass}`}
                                        style={{
                                            minHeight: `${railMinHeight}px`,
                                        }}
                                    >
                                        {is_movable && (
                                            <button
                                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white opacity-100 transition-all ease-in hover:bg-neutral-600"
                                                title="Move Up"
                                                onClick={() =>
                                                    editor.components.move({
                                                        index,
                                                        above_or_below: -1,
                                                    })
                                                }
                                            >
                                                <ArrowUpIcon className="h-3.5 w-3.5" />
                                            </button>
                                        )}

                                        {showShapeDividerButton && (
                                            <button
                                                className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white opacity-100 transition-all ease-in hover:bg-[#8F52F1]"
                                                title="Add or Edit Shape Divider Above"
                                                onClick={
                                                    handleBeforeShapeDividerAction
                                                }
                                            >
                                                <ShapeDividerIcon />
                                            </button>
                                        )}

                                        {is_movable && (
                                            <button
                                                className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white opacity-100 transition-all ease-in hover:bg-[#2CE080] hover:text-neutral-950"
                                                title="Duplicate"
                                                onClick={() =>
                                                    editor.components.duplicate(
                                                        {
                                                            index,
                                                        }
                                                    )
                                                }
                                            >
                                                <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                                            </button>
                                        )}

                                        <button
                                            className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary-700 text-white opacity-100 transition-all ease-in hover:bg-primary-600 disabled:bg-neutral-500"
                                            title={'Edit'}
                                            onClick={() => {
                                                editor.components.add_panel.close();
                                                editor.components.open({
                                                    index,
                                                });
                                            }}
                                        >
                                            <PencilIcon className="h-3.5 w-3.5" />
                                        </button>

                                        {is_movable && (
                                            <button
                                                className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white opacity-100 transition-all ease-in hover:bg-red-600"
                                                title="Delete"
                                                onClick={() =>
                                                    editor.components.delete({
                                                        index,
                                                    })
                                                }
                                            >
                                                <TrashIcon className="h-3.5 w-3.5" />
                                            </button>
                                        )}

                                        {showShapeDividerButton && (
                                            <button
                                                className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white opacity-100 transition-all ease-in hover:bg-[#8F52F1]"
                                                title="Add or Edit Shape Divider Below"
                                                onClick={
                                                    handleAfterShapeDividerAction
                                                }
                                            >
                                                <ShapeDividerIcon flipped />
                                            </button>
                                        )}

                                        {is_movable && (
                                            <button
                                                className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white opacity-100 transition-all ease-in hover:bg-neutral-600"
                                                title="Move Down"
                                                onClick={() =>
                                                    editor.components.move({
                                                        index,
                                                        above_or_below: 1,
                                                    })
                                                }
                                            >
                                                <ArrowDownIcon className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                    {isFooterComponent && (
                        <div
                            className={railContainerClass}
                            style={railContainerStyle}
                        >
                            <div className="pointer-events-auto absolute inset-y-0 left-0 w-32" />
                            <div
                                className={`${railPositionClass} left-0 mr-auto shrink-0 ${railStackClass} ${railHiddenClass}`}
                                style={railStickyStyle}
                            >
                                <div className={railLabelWrapCenteredClass}>
                                    <span
                                        className={railLabelClass}
                                        style={{
                                            height: `${railBadgeHeight}px`,
                                        }}
                                    >
                                        <span className={railLabelTextClass}>
                                            {componentLabel}
                                        </span>
                                    </span>
                                </div>
                                <div
                                    className={`pointer-events-auto ${railShellCenteredClass}`}
                                    style={{
                                        minHeight: `${railMinHeight}px`,
                                    }}
                                >
                                    {showShapeDividerButton && (
                                        <button
                                            className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-neutral-700 text-white transition-all ease-in hover:bg-[#8F52F1]"
                                            title="Add or Edit Shape Divider"
                                            onClick={
                                                handleBeforeShapeDividerAction
                                            }
                                        >
                                            <ShapeDividerIcon />
                                        </button>
                                    )}
                                    <button
                                        className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary-700 text-white transition-all ease-in hover:bg-primary-600 disabled:bg-neutral-500"
                                        title="Edit"
                                        onClick={() => {
                                            editor.components.add_panel.close();
                                            editor.components.open({ index });
                                        }}
                                    >
                                        <PencilIcon className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        className={`pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 transition-all ease-in ${
                                            popupComponentIndex >= 0 &&
                                            popupEnabled
                                                ? 'bg-[#2CE080] text-neutral-950 hover:bg-[#24c970]'
                                                : 'bg-neutral-700 text-white hover:bg-neutral-600'
                                        }`}
                                        title={
                                            popupComponentIndex >= 0
                                                ? 'Edit Popup'
                                                : 'Add Popup'
                                        }
                                        onClick={() => {
                                            editor.components.add_panel.close();
                                            if (popupComponentIndex >= 0) {
                                                editor.components.open({
                                                    index: popupComponentIndex,
                                                });
                                                return;
                                            }
                                            editor.components.add({
                                                index,
                                                component_name: 'Popup',
                                            });
                                            editor.components.open({ index });
                                        }}
                                    >
                                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        className={`pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/10 transition-all ease-in ${
                                            floatingActionComponentIndex >= 0 &&
                                            floatingActionEnabled
                                                ? 'bg-[#2CE080] text-neutral-950 hover:bg-[#24c970]'
                                                : 'bg-neutral-700 text-white hover:bg-neutral-600'
                                        }`}
                                        title={
                                            floatingActionComponentIndex >= 0
                                                ? 'Edit Floating Action'
                                                : 'Add Floating Action'
                                        }
                                        onClick={() => {
                                            editor.components.add_panel.close();
                                            if (
                                                floatingActionComponentIndex >=
                                                0
                                            ) {
                                                editor.components.open({
                                                    index: floatingActionComponentIndex,
                                                });
                                                return;
                                            }
                                            editor.components.add({
                                                index,
                                                component_name:
                                                    'FloatingAction',
                                            });
                                            editor.components.open({ index });
                                        }}
                                    >
                                        <BoltIcon className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <InlineEditProvider value={inlineEditValue}>
                <Component
                    {...component.props}
                    __previousColorScheme={
                        previousComponent?.props?.ColorScheme
                    }
                    __nextColorScheme={nextComponent?.props?.ColorScheme}
                    {...(component.name === 'Nav'
                        ? { company: editor.global.data }
                        : null)}
                    {...(isReviewFloatComponent
                        ? { EditorControls: reviewFloatEditorControls }
                        : null)}
                    editor_component_file={component.data_file}
                    EditorPreview
                    is_edit_mode
                />
            </InlineEditProvider>
            <InlineImageControls
                rootRef={wrapperRef}
                componentProps={component.props}
                imageWarnings={imageWarnings}
                onReplace={handleImageReplace}
                onUpload={handleImageUpload}
                onSeoWarning={handleImageSeoWarning}
            />
        </div>
    );
}
